import React, { useState } from 'react';
import { 
  Camera,
  Upload, 
  Loader2, 
  CheckCircle,
  Home,
  MapPin,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  Hammer,
  Wrench,
  Paintbrush2,
  Zap,
  Droplets,
  TreePine,
  AlertCircle,
  Plus,
  X
} from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedInput,
  BrandedSelect,
  BrandedTextarea,
  BrandedBadge,
  BrandedAlert
} from '../../components/branding/BrandedComponents';

interface CustomerProjectFormProps {
  companyName?: string;
  onProjectSubmitted?: (projectId: string) => void;
}

type ProjectCategory = 'kitchen' | 'bathroom' | 'roofing' | 'flooring' | 'painting' | 'electrical' | 'plumbing' | 'hvac' | 'landscaping' | 'general';
type ProjectUrgency = 'emergency' | 'urgent' | 'soon' | 'planning';
type ProjectBudget = 'under-5k' | '5k-15k' | '15k-50k' | '50k-100k' | 'over-100k' | 'consultation';

interface ProjectFormData {
  customerName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  category: ProjectCategory;
  urgency: ProjectUrgency;
  budget: ProjectBudget;
  title: string;
  description: string;
  preferredContactTime: string;
  photos: File[];
}

export const CustomerProjectForm: React.FC<CustomerProjectFormProps> = ({
  companyName = 'HomeExpert Contractors',
  onProjectSubmitted
}) => {
  return (
    <BrandingProvider templateType="business" initialBranding={{ companyName }}>
      <CustomerProjectFormContent 
        companyName={companyName}
        onProjectSubmitted={onProjectSubmitted}
      />
    </BrandingProvider>
  );
};

const CustomerProjectFormContent: React.FC<CustomerProjectFormProps> = ({
  companyName = 'HomeExpert Contractors',
  onProjectSubmitted
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    customerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    category: 'general',
    urgency: 'planning',
    budget: 'consultation',
    title: '',
    description: '',
    preferredContactTime: 'anytime',
    photos: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const categoryOptions = [
    { value: 'kitchen', label: '🍳 Kitchen Remodel', icon: Home },
    { value: 'bathroom', label: '🛁 Bathroom Renovation', icon: Droplets },
    { value: 'roofing', label: '🏠 Roofing & Gutters', icon: Home },
    { value: 'flooring', label: '🪵 Flooring Installation', icon: Home },
    { value: 'painting', label: '🎨 Interior/Exterior Painting', icon: Paintbrush2 },
    { value: 'electrical', label: '⚡ Electrical Work', icon: Zap },
    { value: 'plumbing', label: '🔧 Plumbing Services', icon: Wrench },
    { value: 'hvac', label: '❄️ HVAC Installation/Repair', icon: Home },
    { value: 'landscaping', label: '🌳 Landscaping & Outdoor', icon: TreePine },
    { value: 'general', label: '🔨 General Contracting', icon: Hammer }
  ];

  const urgencyOptions = [
    { value: 'emergency', label: '🚨 Emergency (ASAP)', color: 'error' },
    { value: 'urgent', label: '⏰ Urgent (This Week)', color: 'warning' },
    { value: 'soon', label: '📅 Soon (This Month)', color: 'primary' },
    { value: 'planning', label: '💭 Planning (Future)', color: 'secondary' }
  ];

  const budgetOptions = [
    { value: 'consultation', label: 'Free Consultation First' },
    { value: 'under-5k', label: 'Under $5,000' },
    { value: '5k-15k', label: '$5,000 - $15,000' },
    { value: '15k-50k', label: '$15,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: 'over-100k', label: 'Over $100,000' }
  ];

  const timeOptions = [
    { value: 'anytime', label: 'Anytime' },
    { value: 'morning', label: 'Morning (8AM-12PM)' },
    { value: 'afternoon', label: 'Afternoon (12PM-5PM)' },
    { value: 'evening', label: 'Evening (5PM-8PM)' },
    { value: 'weekend', label: 'Weekends Only' }
  ];

  const handleInputChange = (field: keyof ProjectFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFormData(prev => ({ ...prev, photos: [...prev.photos, ...newFiles] }));
    }
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({ 
      ...prev, 
      photos: prev.photos.filter((_, i) => i !== index) 
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Extract organization ID from current path (same pattern as Support template)
      const pathname = window.location.pathname;
      const orgMatch = pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
      const organizationId = orgMatch ? orgMatch[1] : null;
      
      if (!organizationId) {
        throw new Error('Organization context is required. Please access this form through your organization.');
      }
      
      console.log('🏠 [HOME IMPROVEMENT] Submitting project for organization:', organizationId);
      
      // Prepare project data
      const projectData = {
        organizationId: organizationId,
        customerName: formData.customerName,
        email: formData.email,
        phone: formData.phone,
        address: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}`,
        category: formData.category,
        urgency: formData.urgency,
        budget: formData.budget,
        title: formData.title,
        description: formData.description,
        preferredContactTime: formData.preferredContactTime,
        photos: formData.photos.map(file => file.name)
      };

      // Submit project to API
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/homeimprovement/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(projectData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit project');
      }

      const result = await response.json();
      setProjectId(result.projectId);
      
      // Redirect to waiting room with org prefix preserved
      const orgPrefix = organizationId ? `/${organizationId}` : '';
      window.location.href = `${orgPrefix}/templates/homeimprovement/waiting/${result.projectId}`;
      
      if (onProjectSubmitted) {
        onProjectSubmitted(result.projectId);
      }
    } catch (err) {
      console.error('Error submitting project:', err);
      setError('Failed to submit project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUrgencyColor = (urgency: ProjectUrgency) => {
    const option = urgencyOptions.find(opt => opt.value === urgency);
    return option?.color || 'secondary';
  };

  if (projectId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--brand-background)' }}>
        <BrandedCard className="max-w-md w-full">
          <div className="text-center">
            <CheckCircle size={48} className="mx-auto mb-4" style={{ color: 'var(--brand-success)' }} />
            <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--brand-text-primary)' }}>
              Project Submitted Successfully!
            </h2>
            <p className="mb-4" style={{ color: 'var(--brand-text-secondary)' }}>
              Thank you for choosing {companyName}. Our team will review your project and contact you soon.
            </p>
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--brand-text-secondary)' }}>
                Your Project ID:
              </p>
              <p className="text-2xl font-mono font-bold" style={{ color: 'var(--brand-primary)' }}>
                #{projectId}
              </p>
            </div>
            <p className="text-sm mb-6" style={{ color: 'var(--brand-text-secondary)' }}>
              We've sent a confirmation email to <strong>{formData.email}</strong> with your project details.
            </p>
          </div>
        </BrandedCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-background)' }}>
      {/* Mobile-Optimized Header */}
      <header className="sticky top-0 z-10 border-b backdrop-blur-sm bg-opacity-95" style={{ 
        backgroundColor: 'var(--brand-surface)', 
        borderColor: 'var(--brand-border)' 
      }}>
        <div className="max-w-2xl mx-auto px-4 py-4">
          <BrandedHeader 
            showLogo={true} 
            showTagline={true}
            customTagline="Get Your Free Project Estimate"
          />
        </div>
      </header>

      {/* Mobile-Optimized Form */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        <BrandedCard className="mb-6">
          <div className="flex items-center mb-6">
            <Home size={24} className="mr-3" style={{ color: 'var(--brand-primary)' }} />
            <h2 className="text-xl font-semibold" style={{ color: 'var(--brand-text-primary)' }}>
              Tell Us About Your Project
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Information */}
            <div>
              <h3 className="font-medium mb-4 flex items-center" style={{ color: 'var(--brand-text-primary)' }}>
                <Phone size={18} className="mr-2" />
                Contact Information
              </h3>
              <div className="space-y-4">
                <BrandedInput
                  label="Your Name *"
                  placeholder="John Doe"
                  value={formData.customerName}
                  onChange={(e) => handleInputChange('customerName', e.target.value)}
                  required
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <BrandedInput
                    label="Email Address *"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                  <BrandedInput
                    label="Phone Number *"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Property Address */}
            <div>
              <h3 className="font-medium mb-4 flex items-center" style={{ color: 'var(--brand-text-primary)' }}>
                <MapPin size={18} className="mr-2" />
                Property Address
              </h3>
              <div className="space-y-4">
                <BrandedInput
                  label="Street Address *"
                  placeholder="123 Main Street"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  required
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <BrandedInput
                    label="City *"
                    placeholder="Anytown"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    required
                  />
                  <BrandedInput
                    label="State *"
                    placeholder="CA"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    required
                  />
                  <BrandedInput
                    label="ZIP Code *"
                    placeholder="12345"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Project Category */}
            <div>
              <h3 className="font-medium mb-4 flex items-center" style={{ color: 'var(--brand-text-primary)' }}>
                <Hammer size={18} className="mr-2" />
                Project Type
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {categoryOptions.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleInputChange('category', value)}
                    className={`p-4 rounded-lg border transition-all text-left ${
                      formData.category === value 
                        ? 'border-2' 
                        : 'border hover:border-gray-400'
                    }`}
                    style={{
                      borderColor: formData.category === value ? 'var(--brand-primary)' : 'var(--brand-border)',
                      backgroundColor: formData.category === value ? 'var(--brand-surface)' : 'transparent'
                    }}
                  >
                    <p className="text-sm font-medium" style={{ 
                      color: formData.category === value ? 'var(--brand-primary)' : 'var(--brand-text-primary)' 
                    }}>
                      {label}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-4">
              <BrandedInput
                label="Project Title *"
                placeholder="Kitchen renovation, bathroom update, etc."
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />

              <BrandedTextarea
                label="Project Description *"
                placeholder="Please describe your project in detail. Include dimensions, materials, timeline, and any specific requirements..."
                rows={6}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
              />
            </div>

            {/* Urgency and Budget */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--brand-text-primary)' }}>
                  Timeline *
                </label>
                <div className="space-y-2">
                  {urgencyOptions.map(({ value, label }) => (
                    <label key={value} className="flex items-center">
                      <input
                        type="radio"
                        name="urgency"
                        value={value}
                        checked={formData.urgency === value}
                        onChange={(e) => handleInputChange('urgency', e.target.value)}
                        className="mr-3"
                      />
                      <span className="text-sm" style={{ color: 'var(--brand-text-primary)' }}>
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <BrandedSelect
                label="Budget Range"
                value={formData.budget}
                onChange={(e) => handleInputChange('budget', e.target.value as ProjectBudget)}
                options={budgetOptions}
              />
            </div>

            <BrandedSelect
              label="Preferred Contact Time"
              value={formData.preferredContactTime}
              onChange={(e) => handleInputChange('preferredContactTime', e.target.value)}
              options={timeOptions}
            />

            {/* Photo Upload */}
            <div>
              <h3 className="font-medium mb-4 flex items-center" style={{ color: 'var(--brand-text-primary)' }}>
                <Camera size={18} className="mr-2" />
                Project Photos (Optional)
              </h3>
              <div className="border-2 border-dashed rounded-lg p-6 text-center" style={{ borderColor: 'var(--brand-border)' }}>
                <input
                  type="file"
                  id="photo-upload"
                  className="hidden"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                />
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <Camera size={32} className="mx-auto mb-2" style={{ color: 'var(--brand-text-muted)' }} />
                  <p className="text-sm font-medium" style={{ color: 'var(--brand-text-secondary)' }}>
                    Take or upload photos of your project area
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--brand-text-muted)' }}>
                    Photos help us provide more accurate estimates
                  </p>
                </label>
              </div>
              
              {formData.photos.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {formData.photos.map((file, index) => (
                    <div key={index} className="relative">
                      <div className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Project photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <BrandedAlert variant="error">
                {error}
              </BrandedAlert>
            )}

            {/* Submit Button */}
            <div className="sticky bottom-0 bg-opacity-95 backdrop-blur-sm p-4 -mx-4 -mb-6 rounded-t-lg" style={{ backgroundColor: 'var(--brand-surface)' }}>
              <div className="flex items-center justify-between mb-4">
                <BrandedBadge variant={getUrgencyColor(formData.urgency) as any}>
                  {urgencyOptions.find(opt => opt.value === formData.urgency)?.label}
                </BrandedBadge>
              </div>
              
              <BrandedButton
                type="submit"
                disabled={isSubmitting}
                className="w-full text-lg py-4"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Submitting Project...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2" size={20} />
                    Get Free Estimate
                  </>
                )}
              </BrandedButton>
            </div>
          </form>
        </BrandedCard>
      </main>
    </div>
  );
};